import { useCallback, useEffect, useRef, useState } from 'react'
import {
  CAT_RUNNING_CHARACTER_KEY,
  CAT_RUNNING_MAGNET_DURATION_MS,
  CAT_RUNNING_SHIELD_DURATION_MS,
  CAT_RUNNING_STAGE_KEY,
} from './constants'
import {
  getSharedCatRunningAssets,
  preloadSharedCatRunningAssets,
} from './game/assets'
import { buildHudState, createInitialWorldState, reviveWorldState } from './game/world'
import { updateWorld as stepWorld } from './game/updateWorld'
import { updatePlayerJump } from './game/update/player'
import catRunningSounds from './game/sounds'
import {
  createCatRunningPlayEndRequest,
  createCatRunningReplay,
  createCatRunningReplayFrame,
} from './replayPayload'

const FIXED_STEP_SEC = 1 / 60
const MAX_FRAME_DELTA_SEC = 0.25
const REPLAY_FRAME_SAMPLE_MS = 1000 / 30
const EMPTY_OPTIONS = {}

export default function useCatRunningGame(options) {
  const resolvedOptions = options ?? EMPTY_OPTIONS
  const animationFrameRef = useRef(0)
  const lastFrameTimeRef = useRef(0)
  const accumulatorRef = useRef(0)
  const initialWorldOptionsRef = useRef(resolvedOptions)
  const worldRef = useRef(createInitialWorldState(initialWorldOptionsRef.current))
  const replayEventsRef = useRef([])
  const replayFramesRef = useRef([])
  const lastReplayFrameAtRef = useRef(-REPLAY_FRAME_SAMPLE_MS)
  const replayEventIndexRef = useRef(0)
  const isReplayingRef = useRef(Boolean(resolvedOptions?.replayData || resolvedOptions?.frameReplay))
  const pausedRef = useRef(Boolean(resolvedOptions?.startPaused))
  const hudStateRef = useRef({
    gameStatus: 'running',
    distance: 0,
    churuCount: 0,
    stageKey: CAT_RUNNING_STAGE_KEY,
    selectedCharacterKey: CAT_RUNNING_CHARACTER_KEY,
    reviveUsed: false,
    invincibleMs: 0,
    magnetMs: 0,
  })
  const assetsRef = useRef(getSharedCatRunningAssets())
  const [hudState, setHudState] = useState({
    gameStatus: 'running',
    distance: 0,
    churuCount: 0,
    stageKey: CAT_RUNNING_STAGE_KEY,
    selectedCharacterKey: CAT_RUNNING_CHARACTER_KEY,
    reviveUsed: false,
    invincibleMs: 0,
    magnetMs: 0,
  })
  const [renderState, setRenderState] = useState({
    backgroundReady: false,
    characterReady: false,
    obstacleReady: false,
    itemReady: false,
    uiReady: false,
  })

  const syncHudState = useCallback((world) => {
    const nextHudState = buildHudState(world)
    const previousHudState = hudStateRef.current
    const hasHudChanged =
      previousHudState.gameStatus !== nextHudState.gameStatus ||
      previousHudState.distance !== nextHudState.distance ||
      previousHudState.churuCount !== nextHudState.churuCount ||
      previousHudState.stageKey !== nextHudState.stageKey ||
      previousHudState.selectedCharacterKey !== nextHudState.selectedCharacterKey ||
      previousHudState.reviveUsed !== nextHudState.reviveUsed ||
      previousHudState.invincibleMs !== nextHudState.invincibleMs ||
      previousHudState.magnetMs !== nextHudState.magnetMs

    if (hasHudChanged) {
      hudStateRef.current = nextHudState
      setHudState(nextHudState)
    }
  }, [])

  const updateWorld = useCallback((deltaSec) => {
    const world = worldRef.current
    const prevChuruCount = world.churuCount
    const prevStatus = world.status

    stepWorld(world, deltaSec, assetsRef.current.characterRun.length)

    if (world.churuCount > prevChuruCount) {
      catRunningSounds.claw()
    }
    if (prevStatus === 'running' && world.status === 'gameover') {
      catRunningSounds.stopEffects()
      catRunningSounds.hit()
    }

    if (
      !isReplayingRef.current &&
      worldRef.current.elapsedMs - lastReplayFrameAtRef.current >= REPLAY_FRAME_SAMPLE_MS
    ) {
      replayFramesRef.current.push(createCatRunningReplayFrame(worldRef.current))
      lastReplayFrameAtRef.current = worldRef.current.elapsedMs
    }
  }, [])

  const getEffectsAdapter = useCallback(
    () => ({
      has: (type) => {
        if (type === 'MAGNET') return worldRef.current.magnetMs > 0
        if (type === 'INVINCIBLE') return worldRef.current.invincibleMs > 0
        return false
      },
    }),
    [],
  )

  const pushReplayEvent = useCallback((type) => {
    if (isReplayingRef.current) return

    replayEventsRef.current.push({
      type,
      tMs: Math.round(worldRef.current.elapsedMs),
    })
  }, [])

  const removeLatestReplayEvent = useCallback((type) => {
    for (let index = replayEventsRef.current.length - 1; index >= 0; index -= 1) {
      if (replayEventsRef.current[index].type === type) {
        replayEventsRef.current.splice(index, 1)
        return
      }
    }
  }, [])

  const setPaused = useCallback((value) => {
    pausedRef.current = Boolean(value)
  }, [])

  const jump = useCallback((options = {}) => {
    if (pausedRef.current) return
    const world = worldRef.current
    const willJump = world.player.isGrounded && world.status === 'running'
    updatePlayerJump(world)
    if (willJump) catRunningSounds.jump()
    if (options.skipRecord) return
    pushReplayEvent('jump')
  }, [pushReplayEvent])

  const revive = useCallback((options = {}) => {
    if (worldRef.current.status !== 'gameover' || worldRef.current.reviveUsed) return false

    reviveWorldState(worldRef.current)
    accumulatorRef.current = 0
    syncHudState(worldRef.current)
    if (!options.skipRecord) {
      pushReplayEvent('revive')
    }

    return true
  }, [pushReplayEvent, syncHudState])

  const activateShield = useCallback((options = {}) => {
    if (worldRef.current.status !== 'running' || worldRef.current.invincibleMs > 0) return false

    worldRef.current.invincibleMs = CAT_RUNNING_SHIELD_DURATION_MS
    catRunningSounds.playEffect('INVINCIBLE', CAT_RUNNING_SHIELD_DURATION_MS / 1000, getEffectsAdapter())
    syncHudState(worldRef.current)
    if (!options.skipRecord) {
      pushReplayEvent('shield')
    }

    return true
  }, [getEffectsAdapter, pushReplayEvent, syncHudState])

  const activateMagnet = useCallback((options = {}) => {
    if (worldRef.current.status !== 'running' || worldRef.current.magnetMs > 0) return false

    worldRef.current.magnetMs = CAT_RUNNING_MAGNET_DURATION_MS
    catRunningSounds.playEffect('MAGNET', CAT_RUNNING_MAGNET_DURATION_MS / 1000, getEffectsAdapter())
    syncHudState(worldRef.current)
    if (!options.skipRecord) {
      pushReplayEvent('magnet')
    }

    return true
  }, [getEffectsAdapter, pushReplayEvent, syncHudState])

  const cancelShieldActivation = useCallback(() => {
    worldRef.current.invincibleMs = 0
    removeLatestReplayEvent('shield')
    syncHudState(worldRef.current)
  }, [removeLatestReplayEvent, syncHudState])

  const cancelMagnetActivation = useCallback(() => {
    worldRef.current.magnetMs = 0
    removeLatestReplayEvent('magnet')
    syncHudState(worldRef.current)
  }, [removeLatestReplayEvent, syncHudState])

  useEffect(() => {
    let isMounted = true

    preloadSharedCatRunningAssets().then((nextRenderState) => {
      if (!isMounted) return

      setRenderState((current) => ({ ...current, ...nextRenderState }))
    })

    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    const step = (timestamp) => {
      if (!lastFrameTimeRef.current) {
        lastFrameTimeRef.current = timestamp
        animationFrameRef.current = window.requestAnimationFrame(step)
        return
      }

      const deltaSec = Math.min((timestamp - lastFrameTimeRef.current) / 1000, MAX_FRAME_DELTA_SEC)
      lastFrameTimeRef.current = timestamp
      if (worldRef.current.status === 'running' && !pausedRef.current) {
        accumulatorRef.current += deltaSec

        while (accumulatorRef.current >= FIXED_STEP_SEC) {
          updateWorld(FIXED_STEP_SEC)
          accumulatorRef.current -= FIXED_STEP_SEC
        }
      } else {
        accumulatorRef.current = 0
      }

      const replayEvents = resolvedOptions?.replayData?.events ?? []
      while (replayEventIndexRef.current < replayEvents.length) {
        const nextEvent = replayEvents[replayEventIndexRef.current]
        if (!Number.isFinite(nextEvent?.tMs) || worldRef.current.elapsedMs < nextEvent.tMs) {
          break
        }

        if (nextEvent.type === 'jump') {
          jump({ skipRecord: true })
        } else if (nextEvent.type === 'revive') {
          revive({ skipRecord: true })
        } else if (nextEvent.type === 'shield') {
          activateShield({ skipRecord: true })
        } else if (nextEvent.type === 'magnet') {
          activateMagnet({ skipRecord: true })
        }

        replayEventIndexRef.current += 1
      }

      syncHudState(worldRef.current)

      animationFrameRef.current = window.requestAnimationFrame(step)
    }

    animationFrameRef.current = window.requestAnimationFrame(step)

    return () => {
      window.cancelAnimationFrame(animationFrameRef.current)
      lastFrameTimeRef.current = 0
      accumulatorRef.current = 0
    }
  }, [activateMagnet, activateShield, jump, resolvedOptions?.replayData?.events, revive, syncHudState, updateWorld])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.repeat) return
      if (event.code !== 'Space' && event.code !== 'ArrowUp') return

      event.preventDefault()
      jump()
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [jump])

  useEffect(() => {
    worldRef.current = createInitialWorldState(resolvedOptions)
    accumulatorRef.current = 0
    lastFrameTimeRef.current = 0
    replayEventsRef.current = []
    replayFramesRef.current = [createCatRunningReplayFrame(worldRef.current)]
    lastReplayFrameAtRef.current = 0
    replayEventIndexRef.current = 0
    isReplayingRef.current = Boolean(resolvedOptions?.replayData || resolvedOptions?.frameReplay)
    syncHudState(worldRef.current)
  }, [resolvedOptions, syncHudState])

  const getReplaySnapshot = useCallback(() => {
    const world = worldRef.current
    const finalFrame = createCatRunningReplayFrame(world)
    const recordedFrames = replayFramesRef.current
    const frames =
      recordedFrames.length === 0 || recordedFrames[recordedFrames.length - 1].t !== finalFrame.t
        ? [...recordedFrames, finalFrame]
        : [...recordedFrames]
    const result = {
      distanceM: Math.floor(world.distance),
      churuCount: world.churuCount,
      elapsedMs: Math.round(world.elapsedMs),
      reviveUsed: world.reviveUsed,
    }
    const replay = createCatRunningReplay({ frames, ...result })

    return {
      runSeed: world.runSeed,
      spawnManifest: world.spawnManifest,
      events: [...replayEventsRef.current],
      replay,
      result,
      createPlayEndRequest: (roundToken) =>
        createCatRunningPlayEndRequest({ roundToken, result, replay }),
    }
  }, [])

  return {
    hudState,
    renderState,
    worldRef,
    assetsRef,
    accumulatorRef,
    jump,
    revive,
    activateShield,
    activateMagnet,
    cancelShieldActivation,
    cancelMagnetActivation,
    getReplaySnapshot,
    setPaused,
  }
}
